import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Button, Typography, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { useStyles } from '../styles';
import { ReactComponent as SortIcon } from 'assets/icons/SortIcon.svg';
import { DropdownIcon } from 'assets/svgComponents/DropdownIcon';
import { SortMenu } from './SortMenu';
import { SortType } from '../types';

interface ProjectTabsProps {
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  projectCounts: {
    all: number;
    pending: number;
    past: number;
    current: number;
  };
  onSortChange?: (sortType: SortType) => void;
  sortType?: SortType;
}

export const ProjectTabs: React.FC<ProjectTabsProps> = ({
  activeTab,
  onTabChange,
  projectCounts,
  onSortChange,
  sortType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [tabAnchorEl, setTabAnchorEl] = useState<null | HTMLElement>(null);

  const createTabLabel = (text: string, count: number) => (
    <Box className={classes.customTabLabel}>
      <Typography component="span">{text}</Typography>
      <Box className={`${classes.customCountBadge} custom-count-badge`}>{count < 9 ? `0${count}` : count}</Box>
    </Box>
  );

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (sortType: SortType) => {
    if (onSortChange) {
      onSortChange(sortType);
    }
  };

  const handleTabMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setTabAnchorEl(event.currentTarget);
  };

  const handleTabMenuClose = () => {
    setTabAnchorEl(null);
  };

  const handleTabMenuItemClick = (value: number) => {
    onTabChange({} as React.SyntheticEvent, value);
    handleTabMenuClose();
  };

  const tabOptions = [
    { value: 0, key: 'pending', label: t('projectList.tabs.pending'), count: projectCounts.pending },
    { value: 1, key: 'current', label: t('projectList.tabs.current'), count: projectCounts.current },
    { value: 2, key: 'past', label: t('projectList.tabs.past'), count: projectCounts.past },
    { value: 3, key: 'all', label: t('projectList.tabs.all'), count: projectCounts.all },
  ];

  return (
    <Box className={classes.tabsContainer} data-testid="project-tabs-container">
      {isMobile ? (
        <>
          <Button
            onClick={handleTabMenuClick}
            className={classes.tabDropdown}
            data-testid="project-tab-dropdown-button"
            endIcon={<DropdownIcon />}
          >
            {tabOptions.find(option => option.value === activeTab)?.label}
          </Button>
          <Menu
            anchorEl={tabAnchorEl}
            open={Boolean(tabAnchorEl)}
            onClose={handleTabMenuClose}
            className={classes.dropdownMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {tabOptions.map(option => (
              <MenuItem
                key={option.value}
                data-testid={`project-tab-dropdown-item-${option.key}`}
                onClick={() => handleTabMenuItemClick(option.value)}
                className={classes.dropdownMenuItem}
                selected={activeTab === option.value}
              >
                <Box className={classes.dropdownItemContent}>
                  <Typography component="span">{option.label}</Typography>
                  {/* <Box className={classes.dropdownCountBadge}>
                    {option.count < 9 ? `0${option.count}` : option.count}
                  </Box> */}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Tabs value={activeTab} onChange={onTabChange} className={classes.tabs} data-testid="project-tabs">
          {/* Pending, Current, Past */}
          <Tab
            label={createTabLabel(t('projectList.tabs.pending'), projectCounts.pending)}
            className={classes.tab}
            data-testid="project-tab-pending"
          />
          <Tab
            label={createTabLabel(t('projectList.tabs.current'), projectCounts.current)}
            className={classes.tab}
            data-testid="project-tab-current"
          />
          <Tab
            label={createTabLabel(t('projectList.tabs.past'), projectCounts.past)}
            className={classes.tab}
            data-testid="project-tab-past"
          />
          <Tab
            label={createTabLabel(t('projectList.tabs.all'), projectCounts.all)}
            className={classes.tab}
            data-testid="project-tab-all"
          />
        </Tabs>
      )}
      <Button
        startIcon={isMobile ? undefined : <SortIcon />}
        className={classes.sortButton}
        data-testid="project-sort-button"
        onClick={handleSortClick}
      >
        {isMobile ? <SortIcon /> : t('projectList.actions.sort')}
      </Button>

      <SortMenu anchorEl={sortAnchorEl} onClose={handleSortClose} onSortChange={handleSortChange} sortType={sortType} />
    </Box>
  );
};
